import { Field, FieldRenderProps } from 'react-final-form';
import { connect } from 'react-redux';
import { FC, useCallback, useState } from 'react';
import { Col, Row } from 'antd';
import i18next from 'i18next';
import { Segment } from '~src/interfaces/segment.interface';

import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import { translateContent } from '~src/services/translationServices';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { validatorMaxLength80, validatorRequired } from '~src/services/validators';
import { SectionOpt } from '~src/shared/form/translatableSectionForm/interfaces';
import { remoteSubmit } from '~src/shared/reactFinalForm/remoteSubmit';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import {
  addAttributeOptionForce,
  addAttributeOptionSecure,
  AttrOptionAddI,
} from '~src/modules/attributes/actions/attributes.actions';
import { AttributeI } from '~src/interfaces/attributes.types';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { StoreI } from '~src/rootReducer';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { getCompanyContentLocale } from '~src/services/session';
import { User } from '~src/interfaces/user.interface';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';

import { ChipsWrapper, DescriptionContainer, VisibilityDescription } from './attributeOptionListItem.sc';
import OverrideConfirmationDialog from './OverrideConfirmationDialog';

interface OwnProps {
  t: i18next.TFunction;
  attribute: AttributeI;
}

interface StateProps {
  loadingAddOpt: boolean;
  defaultContentLocale: string;
  contentLocales: { [key: string]: string };
}

interface DispatchProps {
  addAttributeOptionSecure(value: AttrOptionAddI): ApiReduxResp<string[]>;
  addAttributeOptionForce(value: AttrOptionAddI): ApiReduxResp<string[]>;
}

type Props = OwnProps & StateProps & DispatchProps;

const AttributeOptionAdd: FC<Props> = props => {
  const [showAddBox, setShowAddBox] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [overridePeople, setOverridePeople] = useState<User[]>([]);
  const addFormSections: SectionOpt[] = [
    {
      name: 'info',
      fields: [
        {
          name: `attributes.${props.attribute.uuid}`,
          label: 'x.shared.name_and_description_form.input.placeholder.name',
          validator: composeValidators([validatorMaxLength80, validatorRequired]),
          translationValidator: validatorMaxLength80,
        },
      ],
    },
  ];

  const onSubmit = (payload: Record<string, any>) => {
    let peopleArr: string[] | undefined;
    if (payload.users) {
      peopleArr = payload.users.reduce((acc: string[], person: Segment) => {
        acc.push(person.id);
        return acc;
      }, []);
    }

    let segmentIds: string[] | undefined;
    if (payload.segments) {
      segmentIds = payload.segments.reduce((acc: string[], visibleTo: Segment) => {
        acc.push(visibleTo.id);
        return acc;
      }, []);
    }

    const reqData = {
      attributes: payload.attributes,
      users: peopleArr,
      segments: segmentIds,
    };

    if (overridePeople.length < 1) {
      return props.addAttributeOptionSecure(reqData).then(res => {
        if (res.response && res.response.data.error) {
          if (res.response.data.users) {
            setOverridePeople(Object.values(res.response.data.users));
          }
        } else if (!res.error) {
          setShowAddBox(false);
        }
        return res;
      });
    } else {
      setPopupLoading(true);
      return props
        .addAttributeOptionForce(reqData)
        .then(res => {
          setOverridePeople([]);
          setShowAddBox(false);
          return res;
        })
        .finally(() => {
          setPopupLoading(false);
        });
    }
  };

  const toggleAddBox = () => {
    setShowAddBox(s => !s);
  };

  const renderVisibilityInput = useCallback(({ input, meta: { error } }: FieldRenderProps<any>) => {
    const onChangeSegments = (param: any) => input.onChange(param);
    return (
      <ChipsWrapper>
        <PeopleChips
          t={props.t}
          chipsName="segments"
          error={error}
          onChangePeople={onChangeSegments}
          placeHolderKey="x.attribute_option.visibility_segments.placeholder"
          fetchSuggestions={fetchSearchSegments}
        />
      </ChipsWrapper>
    );
  }, []);

  const renderAddUsersInput = useCallback(({ input, meta: { error } }: FieldRenderProps<any>) => {
    const onChangePeople = (param: any) => input.onChange(param);
    return (
      <ChipsWrapper>
        <PeopleChips
          t={props.t}
          chipsName="users"
          selectedPeople={input.value ? input.value : []}
          error={error}
          onChangePeople={onChangePeople}
          filter="user"
          placeHolderKey="x.attribute_option.add_people.placeholder"
        />
      </ChipsWrapper>
    );
  }, []);

  const { t } = props;

  const submitDialog = () => {
    remoteSubmit('addAttributeOption');
  };

  const hideDialog = () => {
    setOverridePeople([]);
  };

  return (
    <Row gutter={30} justify="end">
      <OverrideConfirmationDialog
        headerTextKey="x.attribute_option.override_modal.header"
        titleKey="x.attribute_option.override_modal.title"
        subTitleKey="x.attribute_option.override_modal.sub_title"
        ctaTextKey="x.attribute_option.override_modal.cta_text"
        submitTextKey="x.attribute_option.override_modal.overwrite"
        cancelTextKey="x.attribute_option.override_modal.cancel"
        people={overridePeople}
        show={overridePeople.length > 0}
        onHide={hideDialog}
        onSubmit={submitDialog}
        loading={popupLoading}
        t={props.t}
      />
      {!showAddBox ? (
        <Col>
          <PrimaryButton onClick={toggleAddBox}>
            <span data-translation="x.attributes.details.option.add.button">
              {t('x.attributes.details.option.add.button', {
                attributeName: translateContent(props.attribute.name),
              })}
            </span>
          </PrimaryButton>
        </Col>
      ) : (
        <Col span={24}>
          <TranslatableSectionForm
            t={t}
            form="addAttributeOption"
            sections={addFormSections}
            loadingSave={props.loadingAddOpt}
            submitBtnKey="x.attributes.details.option.add.button_add"
            onSubmit={onSubmit}
            onCancel={toggleAddBox}
            titleKey="x.attributes.details.option.add.title"
            title={t('x.attributes.details.option.add.title', {
              attributeName: translateContent(props.attribute.name),
            })}
            tooltip={{
              key: 'x.attributes.details.option.add.info_box.content',
              txt: t('x.attributes.details.option.add.info_box.content'),
            }}
            inContainer
          >
            <DescriptionContainer data-translation="x.attribute_option.users_add.description">
              {t('x.attribute_option.users_add.description')}
              <VisibilityDescription>
                <HoverInfoIcon
                  infoText={{
                    key: 'x.attribute_option.users_add.hint',
                    txt: t('x.attribute_option.users_add.hint'),
                  }}
                />
              </VisibilityDescription>
            </DescriptionContainer>
            <Field name="users" component={renderAddUsersInput} />
            <DescriptionContainer data-translation="x.attribute_option.visibility_segments.description">
              {t('x.attribute_option.visibility_segments.description')}
              <VisibilityDescription>
                <HoverInfoIcon
                  infoText={{
                    key: 'x.attribute_option.visibility_segments.hint',
                    txt: t('x.attribute_option.visibility_segments.hint'),
                  }}
                />
              </VisibilityDescription>
            </DescriptionContainer>
            <Field name="segments" component={renderVisibilityInput} />
          </TranslatableSectionForm>
        </Col>
      )}
    </Row>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  loadingAddOpt: state.attributes.loadingAddOpt,
  defaultContentLocale: getCompanyContentLocale(state),
  contentLocales: state.user.session.contentLocales,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  addAttributeOptionSecure: value => dispatch(addAttributeOptionSecure(value)),
  addAttributeOptionForce: value => dispatch(addAttributeOptionForce(value)),
});

const AttributeOptionAddC = connect(mapStateToProps, mapDispatchToProps)(AttributeOptionAdd);

export default AttributeOptionAddC;
